import React from 'react';
import { MenuItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import {
  IconChevronRight,
  IconChevronDown,
  IconChevronUp,
  IconExternalLink,
} from '@tabler/icons-react';
import { Switch } from '../ds';

/**
 * User-menu row primitive (#1299, v4 board #1377, DESIGN_SYSTEM.md 8.8).
 *
 * The trailing indicator is what tells the user how a row behaves before
 * they click it:
 *   switch        on/off setting (row is the target; the v4 Switch is visual)
 *   value         chosen-value setting, current value in quiet trailing text
 *   expand        expands IN PLACE (inline accordion on every viewport,
 *                 #1388; chevron down/up, interactive when open)
 *   chevron-right navigates within the app
 *   external      leaves the app
 *   destructive   danger action (tinted text + icon, negative hover tint)
 *
 * dense = nested accordion row: meta-size text + 16px trailing icons,
 * one step smaller than the 14px top-level rows.
 *
 * forwardRef: category rows hand their DOM node to the categories hook for
 * close-and-refocus keyboard behavior.
 */
const MenuRow = React.forwardRef(function MenuRow({
  icon,
  label,
  trailing,
  trailingValue,
  checked,
  expanded = false,
  dense = false,
  destructive = false,
  onClick,
  sx,
  ...rest
}, ref) {
  const trailingNode = (() => {
    switch (trailing) {
      case 'switch':
        return (
          <Switch
            checked={Boolean(checked)}
            tabIndex={-1}
            aria-hidden
            sx={{ ml: 'auto', pointerEvents: 'none' }}
          />
        );
      case 'value':
        return (
          <Typography
            component="span"
            sx={(theme) => ({
              ml: 'auto',
              pl: `${theme.ds.space[4]}px`,
              fontSize: theme.ds.font.dim,
              color: theme.palette.text.dim,
            })}
          >
            {trailingValue}
          </Typography>
        );
      case 'expand':
        return expanded
          ? <IconChevronUp size={16} style={{ marginLeft: 'auto', paddingLeft: 4, flexShrink: 0 }} />
          : <IconChevronDown size={16} style={{ marginLeft: 'auto', paddingLeft: 4, flexShrink: 0, opacity: 0.7 }} />;
      case 'chevron-right':
        return <IconChevronRight size={16} style={{ marginLeft: 'auto', paddingLeft: 4, flexShrink: 0, opacity: 0.5 }} />;
      case 'external':
        return <IconExternalLink size={16} style={{ marginLeft: 'auto', paddingLeft: 4, flexShrink: 0, opacity: 0.5 }} />;
      default:
        return null;
    }
  })();

  const role = trailing === 'switch' ? 'menuitemcheckbox' : 'menuitem';

  return (
    <MenuItem
      ref={ref}
      role={role}
      aria-checked={trailing === 'switch' ? Boolean(checked) : undefined}
      onClick={onClick}
      sx={(theme) => ({
        fontSize: dense ? theme.ds.font.meta : theme.ds.font.row,
        paddingTop: dense ? '9px' : '11px',
        paddingBottom: dense ? '9px' : '11px',
        gap: 0,
        // v4 board: an expanded category row reads as the open state.
        ...(trailing === 'expand' && expanded && {
          backgroundColor: theme.palette.surface.hover,
          '& .tabler-icon-chevron-up': { color: theme.palette.interactive },
        }),
        ...(destructive && {
          color: theme.palette.error.main,
          '&:hover': { bgcolor: theme.palette.tint.negative },
        }),
        ...(typeof sx === 'function' ? sx(theme) : sx),
      })}
      {...rest}
    >
      {icon && (
        <ListItemIcon
          sx={(theme) => ({
            minWidth: '30px',
            color: destructive ? theme.palette.error.main : theme.palette.text.secondary,
          })}
        >
          {icon}
        </ListItemIcon>
      )}
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          sx: (theme) => ({ fontSize: dense ? theme.ds.font.meta : theme.ds.font.row }),
        }}
      />
      {trailingNode}
    </MenuItem>
  );
});

export default MenuRow;
